import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useContentStore } from '@/stores/content.store'
import {
  createHeaderNavigationSnapshot,
  useHeaderNavigationStore,
  type HeaderNavigationItem,
} from '@/stores/headerNavigation.store'
import { useLocaleStore } from '@/stores/locale.store'
import locationModel, { type LocationModel } from '@/models/location'
import trainerModel, { type TrainerModel } from '@/models/trainer'
import type { CptModel } from '@/models/cpt'
import { localizedPath } from '@/router/i18nPath'
import { sortItems } from '@/utils/sortItems'

import type { DesktopNavCard, DesktopNavKey } from '@/layout/partials/desktopNav'

function formatGymDetails(gym: HeaderNavigationItem) {
  return gym.description ?? ''
}

function getTrainerName(trainer: HeaderNavigationItem) {
  return trainer.title
}

function formatTrainerDetails(trainer: HeaderNavigationItem) {
  return trainer.description ?? ''
}

export function useHeaderNavContent() {
  const { t } = useI18n()
  const contentStore = useContentStore()
  const headerNavigationStore = useHeaderNavigationStore()
  const localeStore = useLocaleStore()
  const { gyms, trainers } = storeToRefs(contentStore)
  const {
    gyms: hydratedGyms,
    trainers: hydratedTrainers,
    isHydrated,
  } = storeToRefs(headerNavigationStore)

  const contentGymItems = computed<LocationModel[]>(() => {
    const list = (gyms.value ?? []) as CptModel[]
    return sortItems('menuOrder')(list.map(locationModel))
  })

  const contentTrainerItems = computed<TrainerModel[]>(() => {
    const list = (trainers.value ?? []) as CptModel[]
    return sortItems('menuOrder')(list.map(trainerModel))
  })

  const fallbackNavigation = computed(() => createHeaderNavigationSnapshot(
    contentGymItems.value,
    contentTrainerItems.value,
    {},
    localeStore.currentLocale,
  ))

  const gymItems = computed<HeaderNavigationItem[]>(() =>
    isHydrated.value ? hydratedGyms.value : fallbackNavigation.value.gyms,
  )

  const trainerItems = computed<HeaderNavigationItem[]>(() =>
    isHydrated.value ? hydratedTrainers.value : fallbackNavigation.value.trainers,
  )

  const gymCards = computed<DesktopNavCard[]>(() =>
    gymItems.value.map((gym) => ({
      id: gym.id,
      title: gym.title,
      to: gym.relativeUrl || localizedPath('gym', { slug: gym.slug }),
      description: formatGymDetails(gym),
      ctaLabel: t('menu.gym'),
    }))
  )

  const trainerCards = computed<DesktopNavCard[]>(() =>
    trainerItems.value.map((trainer) => ({
      id: trainer.id,
      title: getTrainerName(trainer),
      to: trainer.relativeUrl || localizedPath('trainer', { slug: trainer.slug }),
      description: formatTrainerDetails(trainer),
      ctaLabel: t('menu.trainer'),
    }))
  )

  const desktopNavCards = computed<Record<DesktopNavKey, DesktopNavCard[]>>(() => ({
    gym: gymCards.value,
    trainer: trainerCards.value,
  }))

  return {
    desktopNavCards,
    formatGymDetails,
    formatTrainerDetails,
    getTrainerName,
    gymItems,
    trainerItems,
  }
}

export default useHeaderNavContent
